import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, LockKeyhole, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const passwordRules = [
    { test: value => value.length >= 8, message: 'Password must be at least 8 characters.' },
    { test: value => /[A-Z]/.test(value), message: 'Password must contain an uppercase letter.' },
    { test: value => /[a-z]/.test(value), message: 'Password must contain a lowercase letter.' },
    { test: value => /\d/.test(value), message: 'Password must contain a number.' },
];

function destinationFrom(location) {
    const from = location.state?.from;
    if (!from?.pathname) return '/dashboard';
    return `${from.pathname}${from.search || ''}${from.hash || ''}`;
}

export default function ChangePassword() {
    const { pendingPasswordChange, completePasswordChange, clearPendingPasswordChange } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [newPassword, setNewPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    if (!pendingPasswordChange?.token) {
        return <Navigate to="/login" state={{ from: location.state?.from }} replace />;
    }

    const handleSubmit = async event => {
        event.preventDefault();
        setError(null);

        const failedRule = passwordRules.find(rule => !rule.test(newPassword));
        if (failedRule) {
            setError(failedRule.message);
            return;
        }
        if (newPassword !== confirmation) {
            setError('New password and confirmation do not match.');
            return;
        }

        setSaving(true);
        try {
            await completePasswordChange(newPassword);
            navigate(destinationFrom(location), { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const message = typeof detail === 'string'
                ? detail
                : detail?.message || 'This password-change session expired. Please sign in again.';
            if (err?.response?.status === 401) {
                clearPendingPasswordChange();
                navigate('/login', { state: { from: location.state?.from }, replace: true });
                return;
            }
            setError(message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#FAFAF5' }}>
            <div className="w-full max-w-md">
                <div className="text-center mb-8 animate-fade-in">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-stone-900 rounded-2xl shadow-lg mb-4">
                        <Sparkles className="w-7 h-7 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        Nurture <span className="text-stone-500">CRM</span>
                    </h1>
                    <p className="text-stone-500 mt-2 text-sm">Set a new password to continue</p>
                </div>

                <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 animate-fade-in stagger-1">
                    <div className="mb-6">
                        <div className="flex items-center gap-2 text-stone-900 mb-2">
                            <LockKeyhole className="w-5 h-5 text-stone-600" />
                            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                                Change your password
                            </h2>
                        </div>
                        <p className="text-sm text-stone-500">
                            Your temporary password has been accepted. Choose a new password before accessing your workspace.
                        </p>
                    </div>

                    {error && (
                        <div data-testid="password-change-error" className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="new-password" className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">
                                New password
                            </label>
                            <input
                                id="new-password"
                                data-testid="change-password-new-input"
                                type="password"
                                value={newPassword}
                                onChange={event => setNewPassword(event.target.value)}
                                autoComplete="new-password"
                                required
                                className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-400"
                            />
                        </div>
                        <div>
                            <label htmlFor="confirm-password" className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">
                                Confirm new password
                            </label>
                            <input
                                id="confirm-password"
                                data-testid="change-password-confirm-input"
                                type="password"
                                value={confirmation}
                                onChange={event => setConfirmation(event.target.value)}
                                autoComplete="new-password"
                                required
                                className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-400"
                            />
                        </div>
                        <p className="text-xs text-stone-500">Use at least 8 characters with uppercase, lowercase, and a number.</p>
                        <button
                            type="submit"
                            data-testid="change-password-submit-btn"
                            disabled={saving}
                            className="w-full py-2.5 bg-stone-900 text-white font-medium rounded-lg hover:bg-stone-800 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? 'Saving...' : 'Set new password'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
